import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { EntityService, SharedService } from '@app/core';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as XLSX from 'xlsx';
import { AppGlobals } from '../../../../app.global';
import { PriceDiscoveryService, Supplier } from '../../services/price-discovery.service';
import { AddRateCardItemsModalComponent } from '../add-rate-card-items-modal/add-rate-card-items-modal.component';
import { AddSupplierRateCardFileModalComponent } from '../add-supplier-rate-card-file-modal/add-supplier-rate-card-file-modal.component';

@Component({
    selector: 'app-pm-suppliers',
    templateUrl: './pm-suppliers.component.html',
    styleUrls: ['./pm-suppliers.component.scss']
})
export class PmSuppliersComponent implements OnInit, OnDestroy {
    isLoading = false;
    isItemsLoading = false;
    selectedEntityId = '';
    suppliers: Supplier[] = [];
    filteredSuppliers: Supplier[] = [];
    selectedSupplier: Supplier | null = null;
    supplierSearchQuery = '';
    itemSearchQuery = '';
    rateCardItems: any[] = [];
    filteredRateCardItems: any[] = [];

    // Pagination
    totalItems = 0;
    pageSize = 10;
    currentPage = 1;

    editingItemId: string | null = null;
    editingItem: any = null;

    @ViewChild('addRateCardItemsModal') addRateCardItemsModal!: AddRateCardItemsModalComponent;
    @ViewChild('addSupplierRateCardFileModal') addSupplierRateCardFileModal!: AddSupplierRateCardFileModalComponent;

    private destroy$ = new Subject<void>();
    private entityChangeSub: Subscription;

    constructor(
        private priceDiscoveryService: PriceDiscoveryService,
        private entityService: EntityService,
        private sharedService: SharedService,
        @Inject(AppGlobals) private appGlobals: AppGlobals
    ) {
        this.pageSize = this.appGlobals.invoicePerPage || 10;
    }

    ngOnInit(): void {
        const initialEntity = this.entityService.getSingleSelectedEntity();
        if (initialEntity) {
            this.selectedEntityId = initialEntity.id || (typeof initialEntity === 'string' ? initialEntity : '');
            if (this.selectedEntityId) {
                this.loadSuppliers();
            }
        }
        this.listenToEntityChanges();
    }

    ngOnDestroy(): void {
        this.entityChangeSub?.unsubscribe();
        this.destroy$.next();
        this.destroy$.complete();
    }

    listenToEntityChanges(): void {
        this.entityChangeSub = this.entityService.singleSelectedEntity$.pipe(takeUntil(this.destroy$)).subscribe(entity => {
                    if (entity) {
                        const id = entity.id || (typeof entity === 'string' ? entity : '');
                        if (id && id !== this.selectedEntityId) {
                            this.selectedEntityId = id;
                            this.loadSuppliers();
                        }
                    }
                });
    }

    loadSuppliers(): void {
        if (!this.selectedEntityId) return;

        this.isLoading = true;
        this.priceDiscoveryService.getSuppliers(this.selectedEntityId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (data: Supplier[]) => {
                    this.suppliers = (data || []).filter(s =>
                        (s.name || '').toLowerCase() !== 'petpooja'
                    );
                    this.filteredSuppliers = [...this.suppliers];
                    if (this.suppliers.length > 0) {
                        this.selectSupplier(this.suppliers[0]);
                    } else {
                        this.selectedSupplier = null;
                        this.rateCardItems = [];
                        this.filteredRateCardItems = [];
                    }
                    this.isLoading = false;
                },
                (error: any) => {
                    console.error('Error loading suppliers', error);
                    this.sharedService.errorAlert(error);
                    this.isLoading = false;
                }
            );
    }

    selectSupplier(supplier: Supplier): void {
        this.selectedSupplier = supplier;
        this.editingItemId = null;
        this.currentPage = 1; // Reset to first page when switching suppliers
        this.loadRateCardItems(supplier.id);
    }

    loadRateCardItems(supplierId: string, search: string = ''): void {
        this.isItemsLoading = true;
        const params = {
            supplierId,
            search: search,
            sortBy: 'itemName',
            sortOrder: 'ASC',
            pageSize: this.pageSize,
            page: this.currentPage
        };

        this.priceDiscoveryService.getSupplierRateCardItems(params, this.selectedEntityId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (res: any) => {
                    this.rateCardItems = res.items || res.data?.items || [];
                    this.filteredRateCardItems = [...this.rateCardItems];

                    const pagination = res.pagination || res.data?.pagination;
                    if (pagination) {
                        this.totalItems = pagination.totalItems || 0;
                        this.currentPage = pagination.page || 1;
                    } else {
                        this.totalItems = this.rateCardItems.length;
                    }

                    this.isItemsLoading = false;
                },
                (error: any) => {
                    console.error('Error loading rate card items', error);
                    this.sharedService.errorAlert(error);
                    this.isItemsLoading = false;
                }
            );
    }

    onSupplierSearch(): void {
        if (!this.supplierSearchQuery) {
            this.filteredSuppliers = [...this.suppliers];
        } else {
            const query = this.supplierSearchQuery.toLowerCase();
            this.filteredSuppliers = this.suppliers.filter(s =>
                (s.name || '').toLowerCase().includes(query) ||
                (s.alias || '').toLowerCase().includes(query)
            );
        }
    }

    onPageChange(page: number): void {
        this.currentPage = page;
        if (this.selectedSupplier) {
            this.loadRateCardItems(this.selectedSupplier.id, this.itemSearchQuery);
        }
    }

    onPageSizeChange(pageSize: number): void {
        this.pageSize = pageSize;
        this.currentPage = 1;
        if (this.selectedSupplier) {
            this.loadRateCardItems(this.selectedSupplier.id, this.itemSearchQuery);
        }
    }

    onItemSearch(): void {
        if (this.selectedSupplier) {
            this.currentPage = 1;
            this.loadRateCardItems(this.selectedSupplier.id, this.itemSearchQuery);
        }
    }

    onAddSupplier(): void {
        this.addSupplierRateCardFileModal.show(this.selectedEntityId);
    }

    onUpdateRateContract(): void {
        if (!this.selectedSupplier) {
            this.sharedService.errorAlert('Please select a supplier first');
            return;
        }
        const hasExistingRateCard = this.totalItems > 0;
        this.addSupplierRateCardFileModal.show(this.selectedEntityId, 'update', this.selectedSupplier.id, this.selectedSupplier.name, hasExistingRateCard);
    }

    onAddItem(): void {
        if (!this.selectedSupplier) {
            this.sharedService.errorAlert('Please select a supplier first');
            return;
        }
        this.addRateCardItemsModal.show();
    }

    onEditItem(item: any): void {
        this.editingItemId = item._id;
        this.editingItem = { ...item };
    }

    onCancelEdit(): void {
        this.editingItemId = null;
        this.editingItem = null;
    }

    onSaveItem(): void {
        if (!this.editingItem || this.isFormInvalid()) return;

        const itemName = this.editingItem.itemName || this.editingItem.item_name;
        const itemCode = this.editingItem.itemCode || this.editingItem.item_code;
        const unit = this.editingItem.unit || '';
        const rate = this.editingItem.rate;

        const payload = {
            rateCardItemId: this.editingItem._id,
            itemName: itemName.trim(),
            itemCode: itemCode ? itemCode.trim() : itemCode,
            rate: Number(rate),
            unit: unit.trim(),
            isTaxApplicable: this.editingItem.isTaxApplicable ?? this.editingItem.is_tax_applicable
        };

        this.isItemsLoading = true;
        this.priceDiscoveryService.updateRateCardItem(payload, this.selectedEntityId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (res: any) => {
                    this.sharedService.successAlert(res.message);
                    this.editingItemId = null;
                    this.editingItem = null;
                    if (this.selectedSupplier) {
                        this.loadRateCardItems(this.selectedSupplier.id, this.itemSearchQuery);
                    }
                },
                (error: any) => {
                    console.error('Error updating item', error);
                    this.sharedService.errorAlert(error);
                    this.isItemsLoading = false;
                }
            );
    }

    isFormInvalid(): boolean {
        if (!this.editingItem) return true;
        return this.isFieldInvalid('itemName') ||
            this.isFieldInvalid('itemCode') ||
            this.isFieldInvalid('unit') ||
            this.isFieldInvalid('rate');
    }

    isFieldInvalid(field: string): boolean {
        if (!this.editingItem) return false;

        switch (field) {
            case 'itemName':
                const itemName = this.editingItem.itemName || this.editingItem.item_name;
                return !itemName || itemName.trim().length === 0 || itemName.trim().length > 50;
            case 'itemCode':
                const itemCode = this.editingItem.itemCode || this.editingItem.item_code;
                return !!itemCode && itemCode.trim().length > 50;
            case 'unit':
                const unit = this.editingItem.unit || '';
                const unitRegex = /^[a-zA-Z\s]*$/;
                return !unit || !unit.trim() || !unitRegex.test(unit.trim()) || unit.trim().length > 10;
            case 'rate':
                const rate = this.editingItem.rate;
                return rate === null || rate === undefined || rate < 0 || rate > 9999999999;
            default:
                return false;
        }
    }

    onUnitKeyPress(event: KeyboardEvent): void {
        const charCode = event.which ? event.which : event.keyCode;
        const charStr = String.fromCharCode(charCode);
        if (!/^[a-zA-Z\s]*$/.test(charStr)) {
            event.preventDefault();
        }
    }

    onToggleFavorite(item: any): void {
        const newStatus = !(item.isFavorite || item.is_favorite);
        const payload = {
            rateCardItemId: item._id,
            isFavorite: newStatus
        };

        this.priceDiscoveryService.updateRateCardItem(payload, this.selectedEntityId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (res: any) => {
                    item.isFavorite = newStatus;
                    item.is_favorite = newStatus;
                    if (res.message) this.sharedService.successAlert(res.message);
                },
                (error: any) => {
                    console.error('Error toggling favorite', error);
                    this.sharedService.errorAlert(error);
                }
            );
    }

    onDeleteItem(item: any): void {
        if (confirm(`Are you sure you want to delete "${item.itemName || item.item_name}"?`)) {
            this.isItemsLoading = true;
            this.priceDiscoveryService.deleteRateCardItem(item._id, this.selectedEntityId)
                .pipe(takeUntil(this.destroy$))
                .subscribe(
                    (res: any) => {
                        this.sharedService.successAlert(res.message);
                        if (this.selectedSupplier) {
                            this.loadRateCardItems(this.selectedSupplier.id, this.itemSearchQuery);
                        }
                    },
                    (error: any) => {
                        console.error('Error deleting item', error);
                        this.sharedService.errorAlert(error);
                        this.isItemsLoading = false;
                    }
                );
        }
    }

    onExport(): void {
        if (!this.selectedSupplier) {
            this.sharedService.errorAlert('Please select a supplier first');
            return;
        }

        this.isLoading = true;
        const params = {
            supplierId: this.selectedSupplier.id,
            sortBy: 'itemName',
            sortOrder: 'ASC',
            pageSize: 10000,
            page: 1
        };

        this.priceDiscoveryService.getSupplierRateCardItems(params, this.selectedEntityId)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (res: any) => {
                    const items = res.items || [];
                    if (items.length === 0) {
                        this.sharedService.errorAlert('No items found to export');
                        this.isLoading = false;
                        return;
                    }

                    this.downloadAsExcel(items);
                    this.isLoading = false;
                },
                (error: any) => {
                    console.error('Error fetching items for export', error);
                    this.sharedService.errorAlert(error);
                    this.isLoading = false;
                }
            );
    }

    private downloadAsExcel(items: any[]): void {
        const exportData = items.map(item => ({
            'Item Name': item.itemName || item.item_name,
            'Item Code': item.itemCode || item.item_code || '',
            'Unit': item.unit,
            'Rate': item.rate,
            'Tax Applicable': item.isTaxApplicable || item.is_tax_applicable ? 'Yes' : 'No'
        }));

        const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);
        const wb: XLSX.WorkBook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Rate Card Items');

        const fileName = `${this.selectedSupplier.name}_Rate_Card_${new Date().toLocaleDateString('en-CA')}.xlsx`;
        XLSX.writeFile(wb, fileName);
    }

    onSupplierAdded(event: any): void {
        this.supplierSearchQuery = '';
        this.itemSearchQuery = '';
        this.loadSuppliers();
        if (this.selectedSupplier) {
            this.loadRateCardItems(this.selectedSupplier.id, '');
        }
    }

    trackBySupplier(index: number, supplier: Supplier): string {
        return supplier.id;
    }

    getSupplierGst(supplier: any): string {
        if (!supplier) return '-';
        const val = supplier.gstNo || supplier.gst_no || supplier.gstNumber || supplier.gst_number || supplier.gstin || supplier.gst;

        if (Array.isArray(val)) {
            if (val.length > 0) {
                const first = val[0];
                if (typeof first === 'string') return first;
                if (first && typeof first === 'object') return first.gstNo || first.gst || first.gst_no || '-';
            }
            return '-';
        }

        if (typeof val === 'string' && val.trim().length > 0) {
            return val.trim();
        }

        if (val && typeof val === 'object') {
            return val.gstNo || val.gst || val.gst_no || '-';
        }

        return '-';
    }

    getSupplierInitials(name: string): string {
        if (!name) return 'S';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
    }

    trackByItem(index: number, item: any): string {
        return item._id || index.toString();
    }
}
